import { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmt } from "@/lib/helpers";
import { Loader2, Wallet, HandCoins, CalendarDays } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, LabelList } from "recharts";

const MONTHS_FY = ["Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec", "Jan", "Feb", "Mar"];
const FY_ORDER = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];

const kfmt = (n) => {
  n = Number(n) || 0;
  if (Math.abs(n) >= 10000000) return `${(n / 10000000).toFixed(n % 10000000 === 0 ? 0 : 1)}Cr`;
  if (Math.abs(n) >= 100000) return `${(n / 100000).toFixed(n % 100000 === 0 ? 0 : 1)}L`;
  if (Math.abs(n) >= 1000) return `${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}k`;
  return `${n}`;
};

const SEGMENTS = [
  { key: "total_fees", label: "School Fees", color: "#4F46E5" },
  { key: "transport", label: "Transport", color: "#0EA5E9" },
  { key: "old_fees", label: "Old Fees", color: "#8B5CF6" },
  { key: "discount", label: "Discount", color: "#F59E0B" },
  { key: "gross_total", label: "Gross Total", color: "#6366F1" },
  { key: "paid_fees", label: "Paid Fees", color: "#10B981" },
  { key: "due", label: "Due", color: "#E11D48" },
];

export default function ViewFees() {
  const [d, setD] = useState(null);
  const [clsMode, setClsMode] = useState("yearly");
  const [clsMonth, setClsMonth] = useState(() => { const i = FY_ORDER.indexOf(new Date().getMonth() + 1); return i < 0 ? 0 : i; });

  useEffect(() => { api.get("/fees/summary").then((r) => setD(r.data)).catch(() => setD({})); }, []);

  if (!d) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-indigo-500" /></div>;

  const totalFeesAll = (d.total_fees || 0) + (d.transport || 0) + (d.old_fees || 0);
  const grossTotal = Number(d.gross_total || 0);
  const PIE_KEYS = [
    { key: "total_fees", label: "School Fees", color: "#4F46E5" },
    { key: "transport", label: "Transport", color: "#0EA5E9" },
    { key: "old_fees", label: "Old Fees", color: "#8B5CF6" },
    { key: "discount", label: "Discount", color: "#F59E0B" },
    { key: "paid_fees", label: "Paid Fees", color: "#10B981" },
    { key: "due", label: "Due", color: "#E11D48" },
  ];
  const pieAll = PIE_KEYS.map((s) => {
    const value = Number(d[s.key] || 0);
    const grossBasis = s.key === "paid_fees" || s.key === "due";
    const base = grossBasis ? grossTotal : totalFeesAll;
    return { name: s.label, value, color: s.color, pct: base > 0 ? (value / base) * 100 : 0, basis: grossBasis ? "Gross Total" : "Total Fees" };
  });
  const pieData = pieAll.filter((x) => x.value > 0);
  const paidDueData = [
    { name: "Paid Fees", value: Number(d.paid_fees || 0), color: "#10B981" },
    { name: "Due", value: Number(d.due || 0), color: "#E11D48" },
  ].map((x) => ({ ...x, pct: grossTotal > 0 ? (x.value / grossTotal) * 100 : 0 })).filter((x) => x.value > 0);
  const classChartData = (d.class_collection || []).map((c) => ({ class_name: c.class_name, value: clsMode === "yearly" ? c.yearly : ((c.by_month || [])[clsMonth] || 0) }));
  const kpis = [];
  SEGMENTS.forEach((s) => {
    const entry = { ...s, value: d[s.key] || 0 };
    if ((s.key === "paid_fees" || s.key === "due") && grossTotal > 0) entry.pct = ((d[s.key] || 0) / grossTotal) * 100;
    kpis.push(entry);
    if (s.key === "old_fees") kpis.push({ key: "total_fees_all", label: "Total Fees", color: "#EC4899", value: totalFeesAll });
  });

  return (
    <div className="space-y-6" data-testid="view-fees-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-indigo-600 text-white flex items-center justify-center"><Wallet size={20} /></div>
        <div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-900">View Fees</h1>
          <p className="text-sm text-slate-400">Overall fee snapshot across {d.student_count || 0} active students</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        {kpis.map((s) => (
          <div key={s.key} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`view-fees-kpi-${s.key}`}>
            <div className="w-2.5 h-2.5 rounded-full mb-2" style={{ backgroundColor: s.color }} />
            <div className="text-xs text-slate-400 font-medium">{s.label}</div>
            <div className="text-lg font-heading font-extrabold text-slate-900 tabular">{fmt(s.value)}</div>
            {s.pct != null && <div className="text-[11px] text-slate-400 mt-0.5"><span className="font-semibold" style={{ color: s.color }}>{s.pct.toFixed(1)}%</span> of Gross Total</div>}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="view-fees-collections">
        <CollectionKPI label="Today's Collection" value={d.today_collection || 0} total={d.gross_total || 0} tone="emerald" icon={HandCoins} />
        <CollectionKPI label="Monthly Collection" value={d.month_collection || 0} total={d.gross_total || 0} tone="indigo" icon={CalendarDays} />
        <CollectionKPI label="Total Collection" value={d.total_collection || 0} total={d.gross_total || 0} tone="violet" icon={Wallet} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="view-fees-monthly-chart">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-bold text-slate-900">Yearly Collection — Month-wise</h3>
            <span className="text-xs px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-600 font-medium">Apr – Mar</span>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={d.monthly_collection || []} margin={{ left: -8, right: 16, top: 28 }}>
              <defs>
                <linearGradient id="collGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#6366F1" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#6366F1" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={kfmt} tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={46} domain={[0, (max) => Math.ceil((max * 1.2) / 100) * 100]} />
              <Tooltip formatter={(v) => fmt(v)} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 13 }} />
              <Area type="monotone" dataKey="amount" name="Collection" stroke="#6366F1" strokeWidth={2.5} fill="url(#collGrad)" dot={{ r: 3, fill: "#6366F1", strokeWidth: 0 }} activeDot={{ r: 5 }}>
                <LabelList dataKey="amount" position="top" offset={10} formatter={(v) => (v ? fmt(v) : "")} style={{ fontSize: 10, fontWeight: 700, fill: "#4F46E5" }} />
              </Area>
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="view-fees-class-chart">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-bold text-slate-900">Collection — Class-wise</h3>
            <div className="flex items-center gap-2">
              <div className="flex bg-slate-100 rounded-lg p-0.5 text-xs font-medium">
                {[["yearly", "Yearly"], ["monthly", "Monthly"]].map(([k, l]) => (
                  <button key={k} onClick={() => setClsMode(k)} className={`px-3 py-1 rounded-md transition-colors ${clsMode === k ? "bg-white text-indigo-600 shadow-sm" : "text-slate-500 hover:text-slate-700"}`} data-testid={`class-chart-${k}`}>{l}</button>
                ))}
              </div>
              {clsMode === "monthly" && (
                <select value={clsMonth} onChange={(e) => setClsMonth(Number(e.target.value))} className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white text-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-200" data-testid="class-chart-month">
                  {MONTHS_FY.map((m, i) => <option key={m} value={i}>{m}</option>)}
                </select>
              )}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={classChartData} margin={{ left: -8, right: 8, top: 24 }}>
              <defs>
                <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#8B5CF6" stopOpacity={0.95} />
                  <stop offset="100%" stopColor="#6366F1" stopOpacity={0.7} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="class_name" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} interval={0} angle={-20} textAnchor="end" height={54} />
              <YAxis tickFormatter={kfmt} tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={46} />
              <Tooltip formatter={(v) => fmt(v)} cursor={{ fill: "#f8fafc" }} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 13 }} />
              <Bar dataKey="value" name={clsMode === "yearly" ? "Yearly" : MONTHS_FY[clsMonth]} fill="url(#barGrad)" radius={[6, 6, 0, 0]} maxBarSize={52}>
                <LabelList dataKey="value" position="top" offset={6} formatter={(v) => (v ? fmt(v) : "")} style={{ fontSize: 10, fontWeight: 700, fill: "#6D28D9" }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
      <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="view-fees-composition">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className="font-heading font-bold text-slate-900">Fee Composition</h3>
          <span className="text-xs px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-600 font-medium">Total Fees (100%) = {fmt(totalFeesAll)}</span>
        </div>
        {pieData.length === 0 ? (
          <div className="text-center py-16 text-slate-400">No fee data available yet.</div>
        ) : (
          <ResponsiveContainer width="100%" height={360}>
            <PieChart>
              <Pie data={pieData} dataKey="value" nameKey="name" outerRadius={130} innerRadius={60}
                label={(e) => `${e.name} ${(e.pct || 0).toFixed(1)}%`} labelLine={false}>
                {pieData.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Pie>
              <Tooltip formatter={(v, n, item) => [`${fmt(v)} (${(item.payload.pct || 0).toFixed(1)}% of ${item.payload.basis || "Total Fees"})`, n]} />
              <Legend formatter={(value, entry) => `${value} ${entry && entry.payload && entry.payload.pct ? entry.payload.pct.toFixed(1) + "%" : ""}`} />
            </PieChart>
          </ResponsiveContainer>
        )}
        {pieData.length > 0 && (
          <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5" data-testid="composition-values">
            {pieAll.map((s) => (
              <div key={s.name} className="flex items-center gap-2 text-sm">
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
                <span className="text-slate-500">{s.name}</span>
                <span className="ml-auto tabular font-semibold text-slate-800">{fmt(s.value)}</span>
                <span className="text-slate-400 tabular w-16 text-right">{s.pct.toFixed(1)}% <span className="text-[10px]">{s.basis === "Gross Total" ? "GT" : "TF"}</span></span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="view-fees-paid-due-chart">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className="font-heading font-bold text-slate-900">Paid vs Due</h3>
          <span className="text-xs px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-600 font-medium">Gross Total (100%) = {fmt(grossTotal)}</span>
        </div>
        {paidDueData.length === 0 ? (
          <div className="text-center py-16 text-slate-400">No fee data available yet.</div>
        ) : (
          <ResponsiveContainer width="100%" height={360}>
            <PieChart>
              <Pie data={paidDueData} dataKey="value" nameKey="name" outerRadius={130} innerRadius={60}
                label={(e) => `${e.name} ${(e.pct || 0).toFixed(1)}%`} labelLine={false}>
                {paidDueData.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Pie>
              <Tooltip formatter={(v, n, item) => [`${fmt(v)} (${(item.payload.pct || 0).toFixed(1)}% of Gross Total)`, n]} />
              <Legend formatter={(value, entry) => `${value} ${entry && entry.payload && entry.payload.pct ? entry.payload.pct.toFixed(1) + "%" : ""}`} />
            </PieChart>
          </ResponsiveContainer>
        )}
      </div>
      </div>

      {(d.by_class || []).length > 0 && (
        <div className="space-y-3">
          <h3 className="font-heading font-bold text-slate-900">Class-wise Breakdown</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {d.by_class.map((c) => (
              <div key={c.class_name} className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`class-tile-${c.class_name}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="font-heading font-bold text-slate-900">{c.class_name}</div>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600 font-medium">{c.student_count} students</span>
                </div>
                <div className="space-y-1.5 text-sm">
                  <Row label="School Fee" value={c.total_fees} />
                  <Row label="Transport Fees" value={c.transport} />
                  <Row label="Old Fees" value={c.old_fees} />
                  <Row label="Total" value={c.total} cls="text-slate-900 font-semibold" border />
                  <Row label="Discount" value={c.discount} cls="text-amber-600" />
                  <Row label="Gross Total" value={c.gross_total} cls="text-indigo-700 font-semibold" border />
                  <Row label="Paid Fees" value={c.paid_fees} cls="text-emerald-600" />
                  <Row label="Due Fees" value={c.due} cls="text-rose-600 font-semibold" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, cls = "text-slate-800", border }) {
  return (
    <div className={`flex items-center justify-between ${border ? "border-t border-slate-100 pt-1.5 mt-1" : ""}`}>
      <span className="text-slate-500">{label}</span>
      <span className={`tabular ${cls}`}>{fmt(value)}</span>
    </div>
  );
}

function CollectionKPI({ label, value, total, tone, icon: Icon }) {
  const pct = total > 0 ? (value / total) * 100 : 0;
  const tones = {
    emerald: { bg: "bg-emerald-50", text: "text-emerald-600", bar: "bg-emerald-500" },
    indigo: { bg: "bg-indigo-50", text: "text-indigo-600", bar: "bg-indigo-500" },
    violet: { bg: "bg-violet-50", text: "text-violet-600", bar: "bg-violet-500" },
  }[tone];
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`view-fees-collection-${tone}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${tones.bg} ${tones.text}`}><Icon size={18} /></div>
        <div className="text-sm font-medium text-slate-500">{label}</div>
      </div>
      <div className="text-2xl font-heading font-extrabold text-slate-900 tabular">{fmt(value)}</div>
      <div className="mt-3">
        <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className={`h-full ${tones.bar} rounded-full transition-all`} style={{ width: `${Math.min(pct, 100)}%` }} /></div>
        <div className="mt-1.5 text-xs text-slate-400"><span className={`font-semibold ${tones.text}`}>{pct.toFixed(1)}%</span> of Gross Total</div>
      </div>
    </div>
  );
}
